/**
 * KAN-980: ручное начисление или списание кошелька кабинета суперпользователем.
 * Сумма со знаком (минус для списания), комментарий обязателен.
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAdjustPlatformWallet } from '@/hooks/usePlatform';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { normalizeSignedAmount, validateWalletAdjustment } from '@/lib/platform';
import { formatRub } from '@/lib/wallet';
import type { PlatformOrganization } from '@/services/api/platform';

interface WalletDialogProps {
  organization: PlatformOrganization | null;
  onClose: () => void;
}

export function WalletDialog({ organization, onClose }: WalletDialogProps) {
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const adjust = useAdjustPlatformWallet();

  const close = () => {
    setAmount('');
    setComment('');
    setError(null);
    adjust.reset();
    onClose();
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!organization) return;
    const validation = validateWalletAdjustment(amount, comment);
    setError(validation);
    const normalized = normalizeSignedAmount(amount);
    if (validation || normalized === null) return;
    adjust.mutate(
      { orgId: organization.id, payload: { amount: normalized, comment: comment.trim() } },
      { onSuccess: close }
    );
  };

  const serverError = adjust.isError
    ? extractErrorMessage(adjust.error, 'Не удалось изменить баланс, попробуйте ещё раз')
    : null;
  const shownError = error ?? serverError;

  return (
    <Dialog open={organization !== null} onOpenChange={(open) => !open && close()}>
      <DialogContent>
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Начислить / списать</DialogTitle>
            <DialogDescription>
              {organization ? `${organization.name}: на балансе ${formatRub(organization.balance)}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="platform-wallet-amount">Сумма, руб.</Label>
            <Input
              id="platform-wallet-amount"
              data-testid="platform-wallet-amount"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="500 или -100,50"
            />
            <p className="text-xs text-gray-500">Минус для списания</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="platform-wallet-comment">Комментарий</Label>
            <Input
              id="platform-wallet-comment"
              data-testid="platform-wallet-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Причина корректировки"
            />
          </div>
          {shownError && (
            <p data-testid="platform-wallet-error" role="alert" className="text-sm text-red-600">
              {shownError}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>
              Отмена
            </Button>
            <Button type="submit" data-testid="platform-wallet-submit" disabled={adjust.isPending}>
              {adjust.isPending ? 'Сохраняю...' : 'Провести'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
